import { SelectOptionType } from '@montel/montelpro-shared-components-v2';
import {
	createContext,
	FunctionComponent,
	ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import axios from 'axios';
import { BUNDLES_GET_BUNDLE, SUBSCRIPTIONS_API_ROUTE } from 'apiRoutes';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';
import {
	GeoNode,
	PermissionTemplate,
} from 'domain/subscription/interfaces/Subscription';
import { getAddonOptions, getPlanOptions } from '../options';
import { SubscriberType } from 'domain/subscription/interfaces/SubscriberType';
import { isEligibleSubscriberType } from 'domain/subscription/isEligibleSubscriberType';
import { ApplicationPermissions } from '../SubscriptionFormContext';
import { extractLeafNodes } from 'utils/geoNodeHelpers';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { useIsCompanyPayingCustomer } from 'hooks/useIsCompanyPayingCustomer';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums';

export type PermissionsFormContextType = {
	availableApplications: SelectOptionType[];
	formState: PermissionsFormState;
	setFormState: (state: PermissionsFormState) => void;
	availablePermissionSetOptions: PermissionSetOptions | undefined;
	availablePermissionTemplateOptions: SelectOptionType[] | undefined;
	permissionTemplates: PermissionTemplate[];
	resetFormState: () => void;
	onSave: () => void;
	editingPermissions: ApplicationPermissions | undefined;
	preSelectedApplication: boolean;
	bundleHasAreas: boolean;
	companyIsPayingCustomerForSelectedApplication: boolean;
};

export const PermissionsFormContext =
	createContext<PermissionsFormContextType | null>(null);

export interface PermissionsFormState {
	application: SelectOptionType | null;
	plan: SelectOptionType | null;
	addons: SelectOptionType[];
	permissionsTemplate: SelectOptionType | null;
	countries: string[];
}

export interface PermissionSetOptions {
	plans: SelectOptionType[];
	addons: SelectOptionType[];
	areas: GeoNode[];
}

const PermissionsFormContextProvider: FunctionComponent<{
	onPermissionsAdded: (appPermissions: ApplicationPermissions) => void;
	onPermissionsEdited: (appPermissions: ApplicationPermissions) => void;
	subscriberType: SubscriberType;
	editingPermissions: ApplicationPermissions | undefined;
	bundleId?: number;
	companyId?: number;
	children: ReactNode;
}> = ({
	children,
	subscriberType,
	onPermissionsAdded,
	onPermissionsEdited,
	editingPermissions,
	bundleId,
	companyId,
}) => {
	const {
		permissionOptions,
		getAddonByCode,
		getPlanByCode,
		getAreaByCode,
		getSubscribableApplicationById,
	} = useGlobalContext();

	const { adminIsRestrictedToRole } = useAuthRoles();
	const isSalesRole = adminIsRestrictedToRole(AdminRole.SALES);

	const { data: bundle, getData: getBundle } =
		useGetOnMount<SubscriptionBundle>(
			bundleId ? `${BUNDLES_GET_BUNDLE}?bundleId=${bundleId}` : '',
		);

	useEffect(() => {
		getBundle();
	}, [bundleId]);

	const [permissionTemplates, setPermissionTemplates] = useState<
		PermissionTemplate[]
	>([]);

	if (!permissionOptions) {
		return <></>;
	}

	const availableApplications: SelectOptionType[] = permissionOptions
		.filter((app) =>
			isEligibleSubscriberType(subscriberType, app.subscriberType),
		)
		.map((app) => ({
			value: app.applicationId,
			label: app.applicationName,
		}));

	const lockedApplicationOption = useMemo(
		() =>
			bundle
				? availableApplications.find(
						(a) => a.value === bundle.productId,
				  ) ?? null
				: null,
		[bundle, availableApplications],
	);

	const [availablePermissionSetOptions, setAvailablePermissionSetOptions] =
		useState<PermissionSetOptions>();

	const [
		availablePermissionTemplateOptions,
		setAvailablePermissionTemplateOptions,
	] = useState<SelectOptionType[]>();

	const initialFormState = {
		application: lockedApplicationOption,
		plan: null,
		providers: [],
		addons: [],
		countries: [],
		permissionsTemplate: null,
	};

	const [formState, setFormState] =
		useState<PermissionsFormState>(initialFormState);

	const { isPayingCustomer: companyIsPayingCustomerForSelectedApplication } =
		useIsCompanyPayingCustomer(
			companyId,
			formState.application ? [formState.application.value] : undefined,
		);

	useEffect(() => {
		if (permissionTemplates.length) return;

		async function getPermissionTemplates() {
			const response = await axios.get(
				`${SUBSCRIPTIONS_API_ROUTE}/GetPermissionTemplates`,
			);

			setPermissionTemplates(response.data);
		}

		getPermissionTemplates();
	}, []);

	useEffect(() => {
		if (formState.application) {
			const permissionSet = permissionOptions.find(
				(po) => po.applicationId === formState.application?.value,
			);
			const bundleAddonCodes =
				bundle?.permissions?.applicationPermissions?.find(
					(ap) => ap.applicationId === formState.application?.value,
				)?.addons ?? [];
			const permissionSetOptions: PermissionSetOptions = {
				plans: getPlanOptions(permissionSet?.plans),
				addons: getAddonOptions(permissionSet?.addons, isSalesRole).map(
					(addon) =>
						bundleAddonCodes.includes(addon.value)
							? {
									...addon,
									label: `${addon.label} (from bundle)`,
									disabled: true,
							  }
							: addon,
				),
				areas: permissionSet?.areas || [],
			};
			setAvailablePermissionSetOptions(permissionSetOptions);

			setAvailablePermissionTemplateOptions(
				getAvailablePermissionTemplateOptions(),
			);
		} else {
			setAvailablePermissionSetOptions(undefined);
			setAvailablePermissionTemplateOptions(undefined);
		}
	}, [formState.application, bundle]);

	useEffect(() => {
		if (editingPermissions) {
			populateFormState(editingPermissions);
		} else {
			resetFormState();
		}
	}, [editingPermissions]);

	useEffect(() => {
		setFormState((prevState) => ({
			...prevState,
			permissionsTemplate: matchingPermissionTemplateOrNull(),
		}));
	}, [formState.countries]);

	const getAvailablePermissionTemplateOptions = () => {
		if (!formState.application) return [];

		const applicationValue = formState.application.value;
		return permissionTemplates
			.filter((template) =>
				Object.keys(template.applicationPermissions).includes(
					applicationValue,
				),
			)
			.map((template) => ({
				value: template.id,
				label: template.name,
			}));
	};

	const hasValue = <T,>(value: T | null | undefined): value is T =>
		value !== null && value !== undefined;

	const stateToApplicationPermissions = (): ApplicationPermissions | null => {
		if (!formState.application) {
			return null;
		}

		const applicationId = formState.application.value;
		const planCode = formState.plan?.value;

		const application = getSubscribableApplicationById(applicationId);
		const plan = getPlanByCode(applicationId, planCode);

		if (!application) {
			return null;
		}

		const addons = [
			...formState.addons.map((addon) =>
				getAddonByCode(applicationId, addon.value),
			),
		].filter(hasValue);

		const countries = formState.countries
			.map((country) => getAreaByCode(applicationId, country))
			.filter(hasValue)
			.flatMap(extractLeafNodes); // Countries are always the lowest level of the GeoNode hierarchy, i.e. the leaf nodes.

		return {
			application,
			plan,
			addons,
			countries,
		};
	};

	const populateFormState = (appPermissions: ApplicationPermissions) => {
		setFormState({
			application: {
				value: appPermissions.application.id,
				label: appPermissions.application.name,
			},
			plan: {
				value: appPermissions.plan.code,
				label: appPermissions.plan.displayName,
			},
			addons: appPermissions.addons.map((addon) => ({
				value: addon.code,
				label: addon.name,
			})),
			permissionsTemplate: matchingPermissionTemplateOrNull(),
			countries: appPermissions.countries.map((area) => area.code),
		});
	};

	const matchingPermissionTemplateOrNull = (): SelectOptionType | null => {
		const appPermissions = stateToApplicationPermissions();

		if (!appPermissions) {
			return null;
		}

		const matchingTemplate = permissionTemplates.find((template) => {
			const permissionSet =
				template.applicationPermissions[appPermissions.application.id];
			if (!permissionSet) {
				return false;
			}

			const countriesMatch =
				permissionSet.countries?.length ===
					appPermissions.countries.length &&
				permissionSet.countries?.every((c) =>
					appPermissions.countries.map((ac) => ac.code).includes(c),
				);

			return countriesMatch;
		});

		if (matchingTemplate) {
			return {
				value: matchingTemplate.id,
				label: matchingTemplate.name,
			};
		}
		return null;
	};

	const resetFormState = () => {
		setFormState({
			...initialFormState,
			application: lockedApplicationOption,
		});
	};

	useEffect(() => {
		if (!editingPermissions) {
			setFormState((prev) => ({
				...prev,
				application: lockedApplicationOption,
			}));
		}
	}, [bundle?.productId]);

	const onSave = () => {
		const appPermissions = stateToApplicationPermissions();
		if (!appPermissions) {
			return;
		}

		if (editingPermissions) {
			onPermissionsEdited(appPermissions);
		} else {
			onPermissionsAdded(appPermissions);
		}
	};

	return (
		<PermissionsFormContext.Provider
			value={{
				availableApplications: lockedApplicationOption
					? [lockedApplicationOption]
					: availableApplications,
				formState,
				setFormState,
				availablePermissionSetOptions,
				availablePermissionTemplateOptions,
				permissionTemplates,
				resetFormState,
				onSave,
				editingPermissions,
				preSelectedApplication: !!lockedApplicationOption,
				bundleHasAreas:
					(bundle?.permissions?.countries?.length ?? 0) > 0,
				companyIsPayingCustomerForSelectedApplication,
			}}
		>
			{children}
		</PermissionsFormContext.Provider>
	);
};

export function usePermissionsFormContext() {
	const context = useContext(PermissionsFormContext);
	if (!context) {
		throw new Error(
			'usePermissionsFormContext must be used within the PermissionsFormContextProvider',
		);
	}
	return context;
}

export default PermissionsFormContextProvider;
